import type { SxProps, Theme } from '@mui/material/styles';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/use-auth';
import { paths } from '@/routes/paths';
import { ChevronDownIcon, GlobeIcon, MenuIcon } from '@/shared/components/icons';
import { Logo, LOGO_SIZE } from '@/shared/components/logo';
import { ThemeToggleButton } from '@/shared/components/theme-toggle-button';
import { useScrollOffset } from '@/shared/hooks/use-scroll-offset';

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'AR' },
] as const;

const HEADER_HEIGHT = { xs: 80, md: 96 };
const DRAWER_WIDTH = 280;

// Mirrors v1's mobile-nav item styling (`nav-mobile-item.tsx`): full-width 48px row,
// text-secondary at rest, text.primary + medium→semibold weight on hover, logical
// inline paddings (start 2.5, end 1.5) so the indent flips correctly under RTL.
const drawerNavItemSx: SxProps<Theme> = {
  borderRadius: 0,
  height: 48,
  width: '100%',
  justifyContent: 'flex-start',
  color: 'text.secondary',
  fontSize: 14,
  fontWeight: 500,
  paddingInlineStart: 2.5,
  paddingInlineEnd: 1.5,
  textTransform: 'none',
  '&:hover': {
    color: 'text.primary',
    fontWeight: 600,
    backgroundColor: 'action.hover',
  },
};

export function Header() {
  const { t, i18n } = useTranslation('landing');
  const navigate = useNavigate();
  const isOffset = useScrollOffset(16);
  const { user } = useAuth();
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const ctaLabel = user ? t('header.startChat') : t('header.login');
  const ctaTo = user ? paths.chat.root : paths.auth.login;

  const currentLang =
    LANGUAGE_OPTIONS.find((opt) => opt.code === i18n.language) ?? LANGUAGE_OPTIONS[0];
  const blurFilter = isOffset ? 'blur(20px)' : 'none';

  const openLangMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setLangAnchor(event.currentTarget);
  };
  const closeLangMenu = () => setLangAnchor(null);
  const pickLanguage = (code: 'ar' | 'en') => {
    void i18n.changeLanguage(code);
    closeLangMenu();
  };

  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);
  const goTo = (to: string) => {
    closeDrawer();
    navigate(to);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="transparent"
      sx={(theme) => ({
        zIndex: theme.zIndex.appBar,
        backgroundColor: isOffset
          ? `color-mix(in srgb, ${theme.palette.background.default} 80%, transparent)`
          : 'transparent',
        backdropFilter: blurFilter,
        WebkitBackdropFilter: blurFilter,
        boxShadow: isOffset
          ? `inset 0 -1px 0 ${theme.palette.divider}, 0 8px 24px -16px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.12)'}`
          : 'none',
        transitionProperty: 'background-color, box-shadow, backdrop-filter',
        transitionDuration: `${theme.transitions.duration.shorter}ms`,
        transitionTimingFunction: theme.transitions.easing.easeInOut,
      })}
    >
      <Container maxWidth="lg" disableGutters sx={{ px: { xs: 2.5, md: 5 } }}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: HEADER_HEIGHT,
            height: HEADER_HEIGHT,
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={openDrawer}
              aria-label={t('header.menu')}
              sx={{
                display: { xs: 'inline-flex', sm: 'none' },
                color: 'text.primary',
                marginInlineStart: '-8px',
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box
              component="button"
              type="button"
              onClick={() => navigate(paths.home)}
              aria-label={t('header.brand')}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <Logo size={LOGO_SIZE.primary} />
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={closeLangMenu}
              slotProps={{
                paper: {
                  sx: { mt: 1, borderRadius: (t) => `${t.shape.borderRadius}px`, minWidth: 140 },
                },
              }}
            >
              {LANGUAGE_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.code}
                  onClick={() => pickLanguage(opt.code)}
                  selected={opt.code === i18n.language}
                  sx={{ fontSize: 14 }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Menu>

            <Button
              size="small"
              onClick={() => navigate(paths.gems.all)}
              sx={{
                color: 'text.primary',
                fontSize: 14,
                display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              {t('header.gems')}
            </Button>

            <ThemeToggleButton />
            <Button
              size="small"
              onClick={openLangMenu}
              startIcon={<GlobeIcon />}
              endIcon={<ChevronDownIcon />}
              aria-haspopup="menu"
              aria-expanded={Boolean(langAnchor)}
              aria-label={t('header.languageLabel')}
              sx={{
                color: 'text.primary',
                fontSize: 14,
                fontWeight: 500,
                px: 1.25,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              {currentLang.short}
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => navigate(ctaTo)}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                backgroundColor: 'text.primary',
                color: 'background.default',
                fontSize: 14,
                fontWeight: 500,
                px: 2.5,
                py: 0.875,
                '&:hover': { backgroundColor: '#000' },
              }}
            >
              {ctaLabel}
            </Button>
          </Stack>
        </Toolbar>
      </Container>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={closeDrawer}
        slotProps={{
          paper: { sx: { width: DRAWER_WIDTH, display: 'flex', flexDirection: 'column' } },
        }}
      >
        <Box sx={{ pt: 3, pb: 2, px: 2.5 }}>
          <Logo size={LOGO_SIZE.compact} />
        </Box>

        <Stack component="nav" sx={{ flex: '1 1 auto', py: 1 }} spacing={0}>
          <Button onClick={() => goTo(paths.gems.all)} sx={drawerNavItemSx}>
            {t('header.gems')}
          </Button>
        </Stack>

        <Box sx={{ p: 2.5 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={() => goTo(ctaTo)}
            sx={{
              backgroundColor: 'text.primary',
              color: 'background.default',
              fontSize: 14,
              fontWeight: 500,
              py: 1.25,
              '&:hover': { backgroundColor: '#000' },
            }}
          >
            {ctaLabel}
          </Button>
        </Box>
      </Drawer>
    </AppBar>
  );
}
